import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

/* ───────────── deterministic pseudo-random helpers ─────────────
   Same photo id always yields the same tilt, so nothing jitters
   on re-render / re-sort. Small djb2-style hash. */
const hash = (str = '') => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) + str.charCodeAt(i);
  return Math.abs(h);
};

/* Rotation range: ±3.2° — enough to feel human, not drunk. */
const seededRotation = (id) => {
  const h = hash(id + 'rot');
  return ((h % 65) / 10) - 3.2;
};

const IMG_RADIUS = 20;
const FRAME_PADDING = 6;
const FRAME_RADIUS = IMG_RADIUS + FRAME_PADDING;

/**
 * A tactile, Polaroid-style photo card.
 *
 * Props:
 *   image       { id, url }     — required
 *   onClick     () => void
 *   isSelected  boolean         — admin-mode selection
 *   index       number          — fallback seed + priority hint
 *   plain       boolean = false — skip rotation (for list view etc.)
 */
export default function PhotoCard({
  image,
  onClick,
  isSelected = false,
  index = 0,
  plain = false,
}) {
  const seed = image.id || String(index);
  const rotation = useMemo(() => (plain ? 0 : seededRotation(seed)), [seed, plain]);

  // Stagger the first ~10 cards, then snap the rest in instantly.
  // Keeps the initial reveal delightful without punishing sort-flips,
  // which remount cards and should feel immediate.
  const delay = Math.min(index * 0.025, 0.25);

  // First 6 cards are likely above the fold — tell the browser to
  // prioritise their bytes over the rest.
  const isAboveFold = index < 6;

  // Before the image blob has decoded, an `<img>` with no intrinsic
  // dimensions collapses to height 0 — which, inside a rotated white
  // polaroid frame, ends up looking like a stack of thin tilted
  // stripes. We hold a default 4:5 portrait aspect on the frame
  // until the image actually loads, so the card is always a real
  // card-shaped object, with a warm shimmer filling the empty space.
  const [loaded, setLoaded] = useState(false);
  const markLoaded = () => setLoaded(true);

  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 20, scale: 0.94, rotate: 0 }}
      animate={{ opacity: 1, y: 0, scale: 1, rotate: rotation }}
      transition={{ type: 'spring', stiffness: 280, damping: 24, mass: 0.7, delay }}
      whileTap={{
        rotate: 0,
        scale: 0.96,
        transition: { type: 'spring', stiffness: 400, damping: 20 },
      }}
      className="relative block w-full focus:outline-none"
      style={{
        transformOrigin: 'center center',
        padding: 9,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      <div
        className="relative bg-white overflow-hidden"
        style={{
          padding: FRAME_PADDING,
          borderRadius: FRAME_RADIUS,
          boxShadow:
            '0 1px 2px rgba(64, 42, 20, 0.08), ' +
            '0 8px 18px rgba(64, 42, 20, 0.16), ' +
            '0 2px 4px rgba(64, 42, 20, 0.08)',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            borderRadius: IMG_RADIUS,
            overflow: 'hidden',
            // Give the frame a definite portrait height until the
            // real image can take over. `undefined` (not `'auto'`)
            // so the element falls back to its child's natural
            // dimensions once loaded.
            aspectRatio: loaded ? undefined : '4 / 5',
          }}
        >
          <img
            src={image.url}
            alt=""
            onLoad={markLoaded}
            onError={markLoaded}
            className="block w-full"
            loading={isAboveFold ? 'eager' : 'lazy'}
            decoding="async"
            fetchPriority={isAboveFold ? 'high' : 'auto'}
            style={{
              objectFit: 'cover',
              borderRadius: IMG_RADIUS,
              display: 'block',
              opacity: loaded ? 1 : 0,
              transition: 'opacity 0.35s ease',
            }}
          />

          <div
            aria-hidden
            className="photocard-shimmer"
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: IMG_RADIUS,
              opacity: loaded ? 0 : 1,
              transition: 'opacity 0.25s ease',
              pointerEvents: 'none',
            }}
          />
        </div>

        {isSelected && (
          <div
            className="absolute top-3 left-3 flex items-center justify-center"
            style={{
              width: 24,
              height: 24,
              borderRadius: 999,
              background: '#44231E',
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            }}
          >
            <Check size={13} color="#FFFFFF" strokeWidth={2.5} />
          </div>
        )}
      </div>
    </motion.button>
  );
}
